import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiHelpCircle } = FiIcons;

const FAQ_DATA = {
  zh: [
    {
      q: '我的文件會上傳到伺服器嗎？',
      a: '不會。Convert Master 所有文件處理都在您的瀏覽器中本地完成，您的文件不會上傳到任何伺服器，100% 保護您的隱私安全。',
    },
    {
      q: 'Convert Master 是免費的嗎？',
      a: '是的，Convert Master 完全免費使用，無需註冊，無需付費，所有功能都可以免費使用，包括 PDF 轉圖片、圖片格式轉換、圖片壓縮和 PDF 壓縮。',
    },
    {
      q: '支援哪些文件格式？',
      a: '支援 PDF、JPG、PNG、WEBP 等常見格式。可以進行 PDF 轉圖片（JPG/PNG）、圖片格式轉換（JPG/PNG/WEBP 互轉）、圖片壓縮、PDF 壓縮等操作。',
    },
    {
      q: '可以批量處理文件嗎？',
      a: '是的，圖片壓縮功能支援批量處理，一次最多可以處理 10 個文件。所有處理都在本地完成，速度快且保護隱私。',
    },
    {
      q: '如何將 PDF 轉換為圖片？',
      a: '選擇「PDF 轉圖片」工具，上傳 PDF 文件，選擇頁面範圍（全部/當前/自定義），設置輸出格式（JPG 或 PNG）和圖片質量，點擊開始轉換即可。',
    },
    {
      q: '文件大小有限制嗎？',
      a: '圖片文件最大支援 50MB，PDF 文件最大支援 100MB。批量處理最多支援 10 個文件同時處理。',
    },
  ],
  en: [
    {
      q: 'Will my files be uploaded to a server?',
      a: 'No. All file processing in Convert Master is done locally in your browser. Your files are never uploaded to any server, providing 100% privacy protection.',
    },
    {
      q: 'Is Convert Master free?',
      a: 'Yes, Convert Master is completely free to use. No registration required, no hidden fees. All features are available for free, including PDF to image, image format conversion, image compression, and PDF compression.',
    },
    {
      q: 'What file formats are supported?',
      a: 'Supports PDF, JPG, PNG, WEBP and other common formats. You can convert PDF to images (JPG/PNG), convert between image formats (JPG/PNG/WEBP), compress images, and compress PDFs.',
    },
    {
      q: 'Can I process files in batch?',
      a: 'Yes, the image compression feature supports batch processing of up to 10 files at once. All processing is done locally for fast and private results.',
    },
    {
      q: 'How do I convert a PDF to an image?',
      a: 'Select the "PDF to Image" tool, upload your PDF, choose the page range (all/current/custom), set the output format (JPG or PNG) and quality, then click Start Conversion.',
    },
    {
      q: 'Are there file size limits?',
      a: 'Images support up to 50MB per file, and PDFs support up to 100MB. Batch processing supports up to 10 files simultaneously.',
    },
  ],
  ja: [
    {
      q: 'ファイルはサーバーにアップロードされますか？',
      a: 'いいえ。Convert Master のすべてのファイル処理はブラウザ内でローカルに行われます。ファイルはいかなるサーバーにもアップロードされず、100% プライバシーが保護されます。',
    },
    {
      q: 'Convert Master は無料ですか？',
      a: 'はい、Convert Master は完全無料です。登録不要、隠れた費用なし。PDF画像変換、画像形式変換、画像圧縮、PDF圧縮を含む全機能が無料でご利用いただけます。',
    },
    {
      q: 'どのファイル形式に対応していますか？',
      a: 'PDF、JPG、PNG、WEBPなどの一般的な形式に対応しています。PDF→画像変換（JPG/PNG）、画像形式変換（JPG/PNG/WEBP相互変換）、画像圧縮、PDF圧縮が可能です。',
    },
    {
      q: 'バッチ処理はできますか？',
      a: 'はい、画像圧縮機能では最大10ファイルのバッチ処理に対応しています。すべての処理はローカルで行われるため、高速かつプライバシー保護が保証されます。',
    },
    {
      q: 'PDFを画像に変換するには？',
      a: '「PDF画像変換」ツールを選択し、PDFをアップロードし、ページ範囲（全て/現在/カスタム）を選択し、出力形式（JPGまたはPNG）と品質を設定して、変換開始をクリックします。',
    },
    {
      q: 'ファイルサイズに制限はありますか？',
      a: '画像は1ファイルあたり最大50MB、PDFは最大100MBに対応しています。バッチ処理は最大10ファイルまで同時処理できます。',
    },
  ],
  es: [
    {
      q: '¿Mis archivos se subirán a un servidor?',
      a: 'No. Todo el procesamiento de archivos en Convert Master se realiza localmente en tu navegador. Tus archivos nunca se suben a ningún servidor, proporcionando 100% de protección de privacidad.',
    },
    {
      q: '¿Convert Master es gratuito?',
      a: 'Sí, Convert Master es completamente gratuito. No se requiere registro, sin costos ocultos. Todas las funciones están disponibles de forma gratuita, incluyendo PDF a imagen, conversión de formato de imagen, compresión de imagen y compresión de PDF.',
    },
    {
      q: '¿Qué formatos de archivo son compatibles?',
      a: 'Soporta PDF, JPG, PNG, WEBP y otros formatos comunes. Puedes convertir PDF a imágenes (JPG/PNG), convertir entre formatos de imagen (JPG/PNG/WEBP), comprimir imágenes y comprimir PDFs.',
    },
    {
      q: '¿Puedo procesar archivos en lote?',
      a: 'Sí, la función de compresión de imágenes soporta el procesamiento por lotes de hasta 10 archivos a la vez. Todo el procesamiento se realiza localmente para resultados rápidos y privados.',
    },
    {
      q: '¿Cómo convierto un PDF a imagen?',
      a: 'Selecciona la herramienta "PDF a Imagen", sube tu PDF, elige el rango de páginas (todas/actual/personalizado), configura el formato de salida (JPG o PNG) y la calidad, luego haz clic en Iniciar Conversión.',
    },
    {
      q: '¿Hay límites de tamaño de archivo?',
      a: 'Las imágenes soportan hasta 50MB por archivo y los PDFs hasta 100MB. El procesamiento por lotes soporta hasta 10 archivos simultáneamente.',
    },
  ],
};

const FAQItem = ({ item, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="border border-gray-200 rounded-lg overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <button
        className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="font-medium text-gray-800 pr-4 text-sm">{item.q}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-gray-500" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed bg-gray-50 border-t border-gray-100">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const { currentLang } = useLanguage();
  const faqs = FAQ_DATA[currentLang] || FAQ_DATA.zh;

  const TITLES = {
    zh: { title: '常見問題', subtitle: '快速找到您需要的答案' },
    en: { title: 'Frequently Asked Questions', subtitle: 'Find answers to common questions' },
    ja: { title: 'よくある質問', subtitle: 'よくある質問への回答' },
    es: { title: 'Preguntas Frecuentes', subtitle: 'Encuentra respuestas a preguntas comunes' },
  };
  const { title, subtitle } = TITLES[currentLang] || TITLES.zh;

  return (
    <motion.section
      className="bg-white rounded-xl p-6 mt-4 border border-gray-200 shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      aria-label="FAQ"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
          <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-2">
        {faqs.map((item, index) => (
          <FAQItem key={index} item={item} index={index} />
        ))}
      </div>
    </motion.section>
  );
};

export default FAQSection;